import { FEATURES, type FeatureKey, hasAnyFeature, hasFeature } from "@/utils/auth/features";

// Conjuntos de features de ACESSO (navegação) que identificam cada perfil de
// menu/rota — nunca features de funcionalidade, para não misturar os dois
// propósitos. A ordem de checagem importa: perfis mais específicos/exclusivos
// vêm primeiro para que um usuário com múltiplas features não seja
// classificado incorretamente.
const TAREFAS_FEATURES: readonly FeatureKey[] = [FEATURES.ACESSAR_REMESSAS];
const EXPEDICAO_FEATURES: readonly FeatureKey[] = [FEATURES.ACESSAR_EXPEDICAO];
const RECEPCAO_FEATURES: readonly FeatureKey[] = [FEATURES.ACESSAR_RECEPCAO];
const PESQUISAR_FEATURES: readonly FeatureKey[] = [FEATURES.ACESSAR_PESQUISAR_REMESSAS];

type UserProfile =
  | "admin"
  | "producao"
  | "expedicao"
  | "recepcao"
  | "consultor"
  | "cliente"
  | "none";

/**
 * Deriva o perfil de navegação do usuário a partir das features habilitadas,
 * substituindo a antiga checagem por `role.nome`.
 *
 * - `acessar_admin` identifica usuário interno (admin, consultor, produção,
 *   recepção e expedição) e tem prioridade: uma flag de cliente ligada por
 *   engano não tira o usuário interno da área administrativa.
 * - `acessar_cliente` identifica a área do cliente.
 *
 * Entre os internos, "admin" e "producao" só se distinguem porque o admin
 * também recebe as features de expedição/recepção (refletindo os itens extras
 * que hoje só aparecem no menu do admin).
 */
function resolveUserProfile(features: readonly string[] | null | undefined): UserProfile {
  if (!hasFeature(features, FEATURES.ACESSAR_ADMIN)) {
    return hasFeature(features, FEATURES.ACESSAR_CLIENTE) ? "cliente" : "none";
  }

  if (hasAnyFeature(features, TAREFAS_FEATURES)) {
    const temAcessoOperacional = hasAnyFeature(features, [
      ...EXPEDICAO_FEATURES,
      ...RECEPCAO_FEATURES,
    ]);

    return temAcessoOperacional ? "admin" : "producao";
  }

  if (hasAnyFeature(features, EXPEDICAO_FEATURES)) return "expedicao";
  if (hasAnyFeature(features, RECEPCAO_FEATURES)) return "recepcao";
  if (hasAnyFeature(features, PESQUISAR_FEATURES)) return "consultor";

  return "none";
}

export type { UserProfile };
export { resolveUserProfile };
